<template>
  <div class="row g g-md-2 justify-content-center">
    <div class="col-md-3">
      <div class="page-info align-self-center text-center text-md-start">
        <template v-if="page.total > 0">
          {{ $t('pagination.showing', { total: page.total, from: page.from, to: page.to }) }}
        </template>
        <template v-if="page.total === 0">
          {{ $t('pagination.no_records') }}
        </template>
      </div>
    </div>

    <div class="col-md-6 text-center" v-if="page.total > 0">
      <div class="d-flex justify-content-center align-items-center gap-2">
        <nav aria-label="pagination">
          <ul class="pagination pagination-sm justify-content-center" style="margin: 0">
            <!-- 最初のページ -->
            <li :class="`page-item ${page.currentPage === 1 ? 'disabled' : ''}`">
              <button class="page-link" type="button" @click="changePage(1)">&lt;&lt;</button>
            </li>

            <!-- 前へ -->
            <li :class="`page-item ${page.currentPage === 1 ? 'disabled' : ''}`">
              <button class="page-link" type="button" @click="changePage(page.currentPage - 1)">&lt;</button>
            </li>

            <!-- 数字 -->
            <li v-for="pageNumber in shownPages" :key="pageNumber" :class="`page-item ${pageNumber === page.currentPage ? 'active' : ''}`">
              <button class="page-link" type="button" @click="changePage(pageNumber)">{{ pageNumber }}</button>
            </li>

            <!-- 次へ -->
            <li :class="`page-item ${page.currentPage === page.lastPage ? 'disabled' : ''}`">
              <button class="page-link" type="button" @click="changePage(page.currentPage + 1)">&gt;</button>
            </li>

            <!-- 最終ページ -->
            <li :class="`page-item ${page.currentPage === page.lastPage ? 'disabled' : ''}`">
              <button class="page-link" type="button" @click="changePage(page.lastPage)">&gt;&gt;</button>
            </li>
          </ul>
        </nav>

        <!-- ページ番号ドロップダウン -->
        <div class="d-flex align-items-center gap-1">
          <label class="text-nowrap mb-0 small">{{ $t('pagination.page') }}</label>
          <select class="form-select form-select-sm" style="width: auto; min-width: 70px;" v-model.number="selectedPage" @change="changePageFromDropdown">
            <option v-for="pageNum in page.lastPage" :key="pageNum" :value="pageNum">{{ pageNum }}</option>
          </select>
          <span class="text-nowrap small">{{ $t('pagination.totalPages', { total: page.lastPage }) }}</span>
        </div>
      </div>
    </div>

    <div class="col-6 col-md-2 offset-md-1" v-if="page.total > 0">
      <select class="form-select form-select-sm" v-model="page.perPage" @change="changeLimit(page.perPage)">
        <option value="25">{{ $t('pagination.per_page.25') }}</option>
        <option value="50">{{ $t('pagination.per_page.50') }}</option>
        <option value="100">{{ $t('pagination.per_page.100') }}</option>
      </select>
    </div>
  </div>
</template>

<script setup>
import Page from '../filters/Page';
import {computed, toRefs, ref, watch} from 'vue';
import Filter from '../filters/Filter';

// Maximum number of pages to show in paginator
const MAX_SHOWN_PAGES = 10;

const props = defineProps({
  modelValue: {
    type: Filter
  },
  page: {
    type: Page,
    required: true,
  }
});

const emit = defineEmits(['change', 'update:modelValue']);

const {modelValue: filter} = toRefs(props);

// Selected page number in dropdown
const selectedPage = ref(props.page.currentPage);

// Update selectedPage when page.currentPage changes
watch(() => props.page.currentPage, (newPage) => {
  selectedPage.value = newPage;
});

/**
 * Change page
 *
 * @param newPageNumber
 */
const changePage = newPageNumber => {
  filter.value.page = newPageNumber;
  emit('update:modelValue', filter.value)
  emit('change', newPageNumber);
}

/**
 * Change page from dropdown
 */
const changePageFromDropdown = () => {
  changePage(selectedPage.value);
}

/**
 * Change limit
 */
const changeLimit = limit => {
  filter.value.limit = limit;
  emit('update:modelValue', filter.value)
  emit('change', 1);
}

// Page numbers to show in paginator
const shownPages = computed(() => {
  const length = MAX_SHOWN_PAGES;
  const end = Math.min(props.page.lastPage, props.page.currentPage + 5);
  const start = Math.max(end - length + 1, 1);
  return Array.from({ length: Math.min(length, props.page.lastPage) }, (_, i) => start + i);
});
</script>
